// src/utils/replace-placeholders.ts
import { vsprintf } from "@pepita/sprintf";
function replacePlaceholders(message, params, delimiter = "%") {
  if (params) {
    if (Array.isArray(params)) {
      message = vsprintf(message, params);
    } else {
      Object.keys(params).forEach((key) => {
        message = message.replace(
          new RegExp(escapeRegExp(`${delimiter}${key}${delimiter}`), "g"),
          params[key]
        );
      });
    }
  }
  return message;
}
function escapeRegExp(str) {
  return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
}

// src/utils/regex.ts
var S_PLURAL_REGEX = /^\w+\: +(.+)$/;
var C_PLURAL_REGEX = /^\s*((\{\s*(\-?\d+[\s*,\s*\-?\d+]*)\s*\})|([\[\]])\s*(-Inf|\-?\d+)\s*,\s*(\+?Inf|\-?\d+)\s*([\[\]]))\s?(.+?)$/;
var I_PLURAL_REGEX = /^\s*(\{\s*(\-?\d+[\s*,\s*\-?\d+]*)\s*\})|([\[\]])\s*(-Inf|\-?\d+)\s*,\s*(\+?Inf|\-?\d+)\s*([\[\]])/;

// src/utils/convert-number.ts
function convertNumber(number) {
  if ("-Inf" === number) {
    return -Infinity;
  } else if ("+Inf" === number || "Inf" === number) {
    return Infinity;
  }
  return parseInt(number, 10);
}

// src/utils/plural-position.ts
function getPluralPositionFn(locale) {
  if ("pt_BR" === locale) {
    locale = "xbr";
  }
  if (locale.length > 3) {
    locale = locale.replace(/(-|_).*/g, "");
  }
  switch (locale) {
    case "bo":
    case "dz":
    case "id":
    case "ja":
    case "jv":
    case "ka":
    case "km":
    case "kn":
    case "ko":
    case "ms":
    case "th":
    case "tr":
    case "vi":
    case "zh":
      return () => {
        0;
      };
    case "af":
    case "az":
    case "bn":
    case "bg":
    case "ca":
    case "da":
    case "de":
    case "el":
    case "en":
    case "eo":
    case "es":
    case "et":
    case "eu":
    case "fa":
    case "fi":
    case "fo":
    case "fur":
    case "fy":
    case "gl":
    case "gu":
    case "ha":
    case "he":
    case "hu":
    case "is":
    case "it":
    case "ku":
    case "lb":
    case "ml":
    case "mn":
    case "mr":
    case "nah":
    case "nb":
    case "ne":
    case "nl":
    case "nn":
    case "no":
    case "om":
    case "or":
    case "pa":
    case "pap":
    case "ps":
    case "pt":
    case "so":
    case "sq":
    case "sv":
    case "sw":
    case "ta":
    case "te":
    case "tk":
    case "ur":
    case "zu":
      return (number) => number == 1 ? 0 : 1;
    case "am":
    case "bh":
    case "fil":
    case "fr":
    case "gun":
    case "hi":
    case "ln":
    case "mg":
    case "nso":
    case "xbr":
    case "ti":
    case "wa":
      return (number) => number === 0 || number == 1 ? 0 : 1;
    case "be":
    case "bs":
    case "hr":
    case "ru":
    case "sr":
    case "uk":
      return (number) => number % 10 == 1 && number % 100 != 11 ? 0 : number % 10 >= 2 && number % 10 <= 4 && (number % 100 < 10 || number % 100 >= 20) ? 1 : 2;
    case "cs":
    case "sk":
      return (number) => number == 1 ? 0 : number >= 2 && number <= 4 ? 1 : 2;
    case "ga":
      return (number) => number == 1 ? 0 : number == 2 ? 1 : 2;
    case "lt":
      return (number) => number % 10 == 1 && number % 100 != 11 ? 0 : number % 10 >= 2 && (number % 100 < 10 || number % 100 >= 20) ? 1 : 2;
    case "sl":
      return (number) => number % 100 == 1 ? 0 : number % 100 == 2 ? 1 : number % 100 == 3 || number % 100 == 4 ? 2 : 3;
    case "mk":
      return (number) => number % 10 == 1 ? 0 : 1;
    case "mt":
      return (number) => number == 1 ? 0 : number === 0 || number % 100 > 1 && number % 100 < 11 ? 1 : number % 100 > 10 && number % 100 < 20 ? 2 : 3;
    case "lv":
      return (number) => number === 0 ? 0 : number % 10 == 1 && number % 100 != 11 ? 1 : 2;
    case "pl":
      return (number) => number == 1 ? 0 : number % 10 >= 2 && number % 10 <= 4 && (number % 100 < 12 || number % 100 > 14) ? 1 : 2;
    case "cy":
      return (number) => number == 1 ? 0 : number == 2 ? 1 : number == 8 || number == 11 ? 2 : 3;
    case "ro":
      return (number) => number == 1 ? 0 : number === 0 || number % 100 > 0 && number % 100 < 20 ? 1 : 2;
    case "ar":
      return (number) => number === 0 ? 0 : number == 1 ? 1 : number == 2 ? 2 : number >= 3 && number <= 10 ? 3 : number >= 11 && number <= 99 ? 4 : 5;
    default:
      return (number) => number == 1 ? 0 : 1;
  }
}

// src/utils/pluralize.ts
var pluralByLanguage;
var getPluralPosition = (value) => {
  if (!pluralByLanguage) {
    pluralByLanguage = getPluralPositionFn(
      document.documentElement.lang || "en"
    );
  }
  return pluralByLanguage(value);
};
function pluralize(message, number) {
  let matches = [], explicitRules = [], standardRules = [];
  message.split("|").forEach((part) => {
    if (C_PLURAL_REGEX.test(part)) {
      matches = part.match(C_PLURAL_REGEX);
      explicitRules[matches[0]] = matches[matches.length - 1];
    } else if (S_PLURAL_REGEX.test(part)) {
      matches = part.match(S_PLURAL_REGEX);
      standardRules.push(matches[1]);
    } else {
      standardRules.push(part);
    }
  });
  for (let rule in explicitRules) {
    if (I_PLURAL_REGEX.test(rule)) {
      matches = rule.match(I_PLURAL_REGEX);
      if (matches[1]) {
        let ns = matches[2].split(",");
        for (let n in ns) {
          if (number == ns[n]) {
            return explicitRules[rule];
          }
        }
      } else {
        let leftNumber = convertNumber(matches[4]), rightNumber = convertNumber(matches[5]);
        if (("[" === matches[3] ? number >= leftNumber : number > leftNumber) && ("]" === matches[6] ? number <= rightNumber : number < rightNumber)) {
          return explicitRules[rule];
        }
      }
    }
  }
  return standardRules[getPluralPosition(number)] || standardRules[0] || void 0;
}

// src/utils/capitalize.ts
function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// src/index.ts
var translations = window.__babelfish || (window.__babelfish = {});
var defaultPlurality = window.__babelfish_plurals || (window.__babelfish_plurals = {});
function getMessage(key, number) {
  const message = key in translations ? translations[key] : key;
  return pluralize(message, number);
}
function transChoice(key, number, params) {
  return replacePlaceholders(getMessage(key, number), params);
}
function trans(key, params, opts) {
  let count = defaultPlurality[key] || 1;
  if (opts) {
    if (opts.context) {
      key = `${key}_${opts.context}`;
    }
    if (typeof opts.count === "number") {
      count = opts.count;
    }
  }
  const value = transChoice(key, count, params);
  if (opts && opts.capitalize) {
    return capitalize(value);
  }
  return value;
}
function add(keys, value) {
  keys.split("|").forEach((key, i) => {
    translations[key] = value;
    defaultPlurality[key] = i + 1;
  });
}
function load(json) {
  Object.keys(json).forEach((keys) => {
    add(keys, json[keys]);
  });
}
export {
  add,
  load,
  trans,
  transChoice
};
//# sourceMappingURL=index.esm.js.map